/**
 * Calendar view selector — the top-level day/week/month switcher. Composes the
 * toolbar (Today / prev / next / month label / sync / create) and the analysis
 * pane, then renders whichever of DayView/WeekView/MonthView the current
 * `timeFilter` selects, forwarding their props.
 *
 * Event handlers (handleEventClick, handleNewEvent, handleBack, handleDeleteEvent)
 * own their state in EventListModule; only the ones this switcher calls
 * (handleNewEvent, handleEventClick, handleCalendarSlotClick, handleCalendarSync)
 * are threaded in as props. getEventBg is a pure lib helper imported directly.
 */
import React from 'react';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import type { SensorDescriptor, SensorOptions, DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import { CalendarProfileView } from '../CalendarProfileView';
import { CalendarSyncButton } from '../components/CalendarSyncButton';
import { getEventBg } from '../lib/eventColors';
import type { EventObject } from '../eventTypes';
import { DayView } from './DayView';
import { WeekView } from './WeekView';
import { MonthView } from './MonthView';

export interface CalendarViewSelectorProps {
  calendarDate: Date;
  setCalendarDate: React.Dispatch<React.SetStateAction<Date>>;
  timeFilter: 'today' | 'week' | 'month';
  setTimeFilterState: React.Dispatch<React.SetStateAction<'today' | 'week' | 'month'>>;
  isMobile: boolean;
  isExpandedView: boolean;
  showAnalysis: boolean;
  calendarConnected: boolean;
  /** #5566: true while the accounts fetch is in flight — keeps the date-nav
   *  toolbar visible during the pre-fetch 'unknown' window (connected resets
   *  non-authoritatively on mount, so gating on it alone hides the toolbar). */
  loadingAccounts: boolean;
  allEvents: EventObject[];
  expandedEvents: EventObject[];
  /** #3952: ids flagged as same-slot suspected duplicates (chip/marker source). */
  suspectedDuplicateIds: Set<string>;
  isSyncingCalendar: boolean;
  draggedEventId: string | null;
  dayWeekBodyHeight: number;
  calendarContainerRef: React.RefObject<HTMLDivElement | null>;
  dayWeekContainerRef: React.RefObject<HTMLDivElement | null>;
  timeGridRef: React.RefObject<HTMLDivElement | null>;
  monthGridRef: React.RefObject<HTMLDivElement | null>;
  sensors: SensorDescriptor<SensorOptions>[];
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  getEventsForDate: (date: Date) => EventObject[];
  getEventsOverlappingDay: (date: Date) => EventObject[];
  isToday: (date: Date) => boolean;
  handleEventClick: (event: EventObject) => void;
  handleNewEvent: () => void;
  handleCalendarSlotClick: (date: Date, hour: number) => void;
  handleCalendarSync: () => void;
  onCompleteTaskEvent: (event: EventObject) => void;
  onOpenTaskEvent: (event: EventObject) => void;
}

export function CalendarViewSelector({
  calendarDate,
  setCalendarDate,
  timeFilter,
  setTimeFilterState,
  isMobile,
  isExpandedView,
  showAnalysis,
  calendarConnected,
  loadingAccounts,
  allEvents,
  expandedEvents,
  suspectedDuplicateIds,
  isSyncingCalendar,
  draggedEventId,
  dayWeekBodyHeight,
  calendarContainerRef,
  dayWeekContainerRef,
  timeGridRef,
  monthGridRef,
  sensors,
  handleDragStart,
  handleDragEnd,
  getEventsForDate,
  getEventsOverlappingDay,
  isToday,
  handleEventClick,
  handleNewEvent,
  handleCalendarSlotClick,
  handleCalendarSync,
  onCompleteTaskEvent,
  onOpenTaskEvent,
}: CalendarViewSelectorProps) {
  const year = calendarDate.getFullYear();
  const month = calendarDate.getMonth();
  const monthNames = isMobile
    ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const getHeaderText = () => {
    if (timeFilter === 'today') {
      return calendarDate.toLocaleDateString('en-US', { weekday: isMobile ? 'short' : 'long', month: isMobile ? 'short' : 'long', day: 'numeric', year: 'numeric' });
    } else if (timeFilter === 'week') {
      const startOfWeek = new Date(calendarDate);
      startOfWeek.setDate(calendarDate.getDate() - calendarDate.getDay());
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      if (startOfWeek.getMonth() === endOfWeek.getMonth()) {
        return `${monthNames[startOfWeek.getMonth()]} ${startOfWeek.getDate()}-${endOfWeek.getDate()}, ${year}`;
      }
      return `${monthNames[startOfWeek.getMonth()]} ${startOfWeek.getDate()} - ${monthNames[endOfWeek.getMonth()]} ${endOfWeek.getDate()}, ${year}`;
    }
    return `${monthNames[month]} ${year}`;
  };

  const navigatePrev = () => {
    if (timeFilter === 'today') {
      setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth(), calendarDate.getDate() - 1));
    } else if (timeFilter === 'week') {
      setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth(), calendarDate.getDate() - 7));
    } else {
      setCalendarDate(new Date(year, month - 1, 1));
    }
  };

  const navigateNext = () => {
    if (timeFilter === 'today') {
      setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth(), calendarDate.getDate() + 1));
    } else if (timeFilter === 'week') {
      setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth(), calendarDate.getDate() + 7));
    } else {
      setCalendarDate(new Date(year, month + 1, 1));
    }
  };

  return (
    <div ref={calendarContainerRef} className="calendar-container" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Calendar nav: ‹ › Month Label  Today  + New  Sync */}
      {/* #5566: also show the toolbar while accounts are still loading — the
          pre-fetch 'unknown' window resets `calendarConnected` to false, and
          gating on it alone made the whole date-nav toolbar vanish for a beat
          on a warm remount landing on an empty view. */}
      {(calendarConnected || loadingAccounts || allEvents.length > 0) && <>
      {/* #4295: the date-nav header only drives the Day/Week/Month grids, which the
          Analysis pane ignores — hide it in the Analysis view so it isn't a dead control. */}
      {!showAnalysis && (
      <div className="gcal-toolbar" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button type="button" className="gcal-today-pill" onClick={() => setCalendarDate(new Date())}>
            Today
          </button>
          <button onClick={navigatePrev} className="module-toolbar-button is-icon-only" title="Previous">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button onClick={navigateNext} className="module-toolbar-button is-icon-only" title="Next">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
          <span className="gcal-toolbar-month">{getHeaderText()}</span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
            {calendarConnected && (
              <CalendarSyncButton
                isSyncing={isSyncingCalendar}
                onSync={handleCalendarSync}
              />
            )}
            <button
              type="button"
              className="calendar-create-btn"
              onClick={handleNewEvent}
              style={{
                background: 'var(--color-surface)',
                border: '1px solid var(--calendar-create-btn-color, #0369a1)',
                borderRadius: '22px',
                boxSizing: 'border-box',
                cursor: 'pointer',
                height: 32,
                padding: '0 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                color: 'var(--calendar-create-btn-color, #0369a1)',
                fontSize: '13px',
                fontWeight: 500,
                flexShrink: 0,
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--calendar-create-btn-hover-bg, #F0F7FB)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-surface)'; }}
              title="New event"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Create
            </button>
          </div>
        </div>
      </div>
      )}

      {showAnalysis ? (
        <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <CalendarProfileView />
        </div>
      ) : (
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        {timeFilter === 'today' && (
          <DayView
            calendarDate={calendarDate}
            isMobile={isMobile}
            isExpandedView={isExpandedView}
            dayWeekBodyHeight={dayWeekBodyHeight}
            dayWeekContainerRef={dayWeekContainerRef}
            timeGridRef={timeGridRef}
            suspectedDuplicateIds={suspectedDuplicateIds}
            getEventsForDate={getEventsForDate}
            getEventsOverlappingDay={getEventsOverlappingDay}
            isToday={isToday}
            handleEventClick={handleEventClick}
            handleCalendarSlotClick={handleCalendarSlotClick}
            onCompleteTaskEvent={onCompleteTaskEvent}
            onOpenTaskEvent={onOpenTaskEvent}
          />
        )}
        {timeFilter === 'week' && (
          <WeekView
            calendarDate={calendarDate}
            isMobile={isMobile}
            isExpandedView={isExpandedView}
            dayWeekBodyHeight={dayWeekBodyHeight}
            dayWeekContainerRef={dayWeekContainerRef}
            timeGridRef={timeGridRef}
            timeFilter={timeFilter}
            suspectedDuplicateIds={suspectedDuplicateIds}
            getEventsForDate={getEventsForDate}
            getEventsOverlappingDay={getEventsOverlappingDay}
            isToday={isToday}
            setCalendarDate={setCalendarDate}
            setTimeFilterState={setTimeFilterState}
            handleEventClick={handleEventClick}
            handleCalendarSlotClick={handleCalendarSlotClick}
            onCompleteTaskEvent={onCompleteTaskEvent}
            onOpenTaskEvent={onOpenTaskEvent}
          />
        )}
        {timeFilter === 'month' && (
          <MonthView
            calendarDate={calendarDate}
            expandedEvents={expandedEvents}
            suspectedDuplicateIds={suspectedDuplicateIds}
            monthGridRef={monthGridRef}
            isToday={isToday}
            handleCalendarSlotClick={handleCalendarSlotClick}
            handleEventClick={handleEventClick}
            setCalendarDate={setCalendarDate}
            setTimeFilterState={setTimeFilterState}
            onCompleteTaskEvent={onCompleteTaskEvent}
            onOpenTaskEvent={onOpenTaskEvent}
          />
        )}
        <DragOverlay>
          {draggedEventId && (() => {
            const event = allEvents.find(e => e.id === draggedEventId);
            if (!event) return null;
            return (
              <div
                className="gcal-event"
                style={{
                  background: getEventBg(event.eventType, false, event.metadata?.customColor),
                  color: 'var(--color-text)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  cursor: 'grabbing',
                }}
              >
                <span className="gcal-event-title">{event.title}</span>
              </div>
            );
          })()}
        </DragOverlay>
      </DndContext>
      )}
      </>}

    </div>
  );
}
